import React, { useCallback, useRef } from 'react';
import { useStore, ReactFlowState } from 'reactflow';
import { X } from 'lucide-react';
import { Swimlane } from '../../types';
import { MIN_LANE_WIDTH, MIN_LANE_HEIGHT } from '../../utils/layoutConstants';

export const DEFAULT_LANE_WIDTH = 1600;
export const DEFAULT_LANE_HEIGHT = 240;
export const LANE_HEADER_WIDTH = 44;
export { MIN_LANE_WIDTH, MIN_LANE_HEIGHT };
export const DEFAULT_LANE_COLOR = '#94a3b8'; // slate-400 (grey)
const REMOVE_BUTTON_ICON_COLOR = '#64748b'; // slate-500

/**
 * Returns the topmost (last-drawn) swimlane whose rectangle contains the point,
 * or null if none. Reverse iteration so visually-on-top lanes win.
 */
export const swimlaneAtPoint = (
  x: number,
  y: number,
  lanes: Swimlane[]
): Swimlane | null => {
  for (let i = lanes.length - 1; i >= 0; i--) {
    const l = lanes[i];
    if (x >= l.x && x <= l.x + l.width && y >= l.y && y <= l.y + l.height) {
      return l;
    }
  }
  return null;
};

const transformSelector = (s: ReactFlowState) => s.transform;

interface SwimlanesProps {
  swimlanes: Swimlane[];
  selectedSwimlaneId: string | null;
  onSelect: (id: string | null) => void;
  onMoveBy: (id: string, dx: number, dy: number) => void;
  onResize: (id: string, width: number, height: number) => void;
  onRemove: (id: string) => void;
}

// Convert hex color to rgba with given alpha (for body fill).
const toRgba = (hex: string, alpha: number): string => {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Swimlanes: React.FC<SwimlanesProps> = ({
  swimlanes,
  selectedSwimlaneId,
  onSelect,
  onMoveBy,
  onResize,
  onRemove,
}) => {
  const [tx, ty, zoom] = useStore(transformSelector);
  const dragRef = useRef<{ id: string; startX: number; startY: number; lastDx: number; lastDy: number } | null>(null);
  const resizeRef = useRef<{ id: string; startX: number; startY: number; origW: number; origH: number } | null>(null);

  const beginMove = useCallback((e: React.MouseEvent, lane: Swimlane) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(lane.id);
    dragRef.current = {
      id: lane.id,
      startX: e.clientX,
      startY: e.clientY,
      lastDx: 0,
      lastDy: 0,
    };
    const onMouseMove = (ev: MouseEvent) => {
      const s = dragRef.current;
      if (!s) return;
      const totalDx = (ev.clientX - s.startX) / zoom;
      const totalDy = (ev.clientY - s.startY) / zoom;
      const stepDx = totalDx - s.lastDx;
      const stepDy = totalDy - s.lastDy;
      s.lastDx = totalDx;
      s.lastDy = totalDy;
      if (stepDx !== 0 || stepDy !== 0) {
        onMoveBy(s.id, stepDx, stepDy);
      }
    };
    const onMouseUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [onSelect, onMoveBy, zoom]);

  const beginResize = useCallback((e: React.MouseEvent, lane: Swimlane) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(lane.id);
    resizeRef.current = {
      id: lane.id,
      startX: e.clientX,
      startY: e.clientY,
      origW: lane.width,
      origH: lane.height,
    };
    const onMouseMove = (ev: MouseEvent) => {
      const s = resizeRef.current;
      if (!s) return;
      const dx = (ev.clientX - s.startX) / zoom;
      const dy = (ev.clientY - s.startY) / zoom;
      const w = Math.max(MIN_LANE_WIDTH, s.origW + dx);
      const h = Math.max(MIN_LANE_HEIGHT, s.origH + dy);
      onResize(s.id, w, h);
    };
    const onMouseUp = () => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [onSelect, onResize, zoom]);

  const transformStyle: React.CSSProperties = {
    transform: `translate(${tx}px, ${ty}px) scale(${zoom})`,
    transformOrigin: '0 0',
    position: 'absolute',
    top: 0,
    left: 0,
  };

  return (
    <>
      {/* Layer 1 — visual body (behind nodes). zIndex 0 puts this above the
          dot-background (z:-1) but below the nodes/edges renderer. */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ zIndex: 0 }}
      >
        <div style={transformStyle}>
          {swimlanes.map((lane) => {
            const color = lane.color || DEFAULT_LANE_COLOR;
            const isSelected = lane.id === selectedSwimlaneId;
            return (
              <div
                key={lane.id + '-bg'}
                style={{
                  position: 'absolute',
                  left: lane.x,
                  top: lane.y,
                  width: lane.width,
                  height: lane.height,
                  background: toRgba(color, 0.12),
                  border: isSelected ? `2px solid ${color}` : `1px solid ${toRgba(color, 0.6)}`,
                  // Bottom-right corner is square so the resize handle sits
                  // cleanly on the border intersection.
                  borderRadius: '8px 8px 0 8px',
                  boxSizing: 'border-box',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Layer 2 — interaction handles (above nodes). Only the header strip,
          remove button, and resize handle receive pointer events. Lane
          interior is not covered, so nodes remain fully interactive. */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ zIndex: 5 }}
      >
        <div style={transformStyle}>
          {swimlanes.map((lane) => {
            const color = lane.color || DEFAULT_LANE_COLOR;
            const isSelected = lane.id === selectedSwimlaneId;
            return (
              <React.Fragment key={lane.id + '-ui'}>
                {/* Header strip (movable) */}
                <div
                  onMouseDown={(e) => beginMove(e, lane)}
                  style={{
                    position: 'absolute',
                    left: lane.x,
                    top: lane.y,
                    width: LANE_HEADER_WIDTH,
                    height: lane.height,
                    background: color,
                    opacity: isSelected ? 0.95 : 0.85,
                    cursor: 'move',
                    pointerEvents: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderTopLeftRadius: 6,
                    borderBottomLeftRadius: 6,
                    boxShadow: isSelected ? `0 0 0 2px ${color}` : 'none',
                  }}
                  title="Drag to move swimlane"
                >
                  <div
                    style={{
                      transform: 'rotate(-90deg)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: '0.05em',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                      textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                    }}
                  >
                    {lane.name}
                  </div>
                </div>

                {/* Remove button */}
                <button
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); onRemove(lane.id); }}
                  style={{
                    position: 'absolute',
                    top: lane.y + 6,
                    left: lane.x + lane.width - 28,
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(148,163,184,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: REMOVE_BUTTON_ICON_COLOR,
                    pointerEvents: 'auto',
                  }}
                  title="Remove swimlane"
                >
                  <X size={12} />
                </button>

                {/* Resize handle (bottom-right). Transparent hit area anchored
                    at the corner so the two 45° stripes land exactly on the
                    bottom and right borders where they meet. */}
                <div
                  onMouseDown={(e) => beginResize(e, lane)}
                  style={{
                    position: 'absolute',
                    // Anchor so the SVG's bottom-right (14,14) sits ON the
                    // lane's bottom-right corner (lane.x+width, lane.y+height).
                    left: lane.x + lane.width - 14,
                    top: lane.y + lane.height - 14,
                    width: 14,
                    height: 14,
                    cursor: 'nwse-resize',
                    pointerEvents: 'auto',
                    background: 'transparent',
                  }}
                  title="Drag to resize"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    style={{ display: 'block' }}
                    fill="none"
                  >
                    {/* Two 45° stripes hugging the corner; each endpoint
                        touches the bottom or right border of the lane. */}
                    <line x1="14" y1="4" x2="4" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
                    <line x1="14" y1="9" x2="9" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Swimlanes;
